import React, { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, File, Download, Eye, Trash2, X, AlertCircle, Plus, Settings, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { attachmentService, DocumentAttachment } from '@/app/services/attachmentService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface DocumentAttachmentsProps {
  entityType: string;
  entityId: number;
  siteId?: number;
  title?: string;
  maxFileSize?: number; // in MB
  allowedFileTypes?: string[];
  maxFiles?: number;
  onUploadComplete?: (attachment: DocumentAttachment) => void;
  onDeleteComplete?: (attachmentId: number) => void;
  className?: string;
  enableMetadata?: boolean;
}

export const DocumentAttachments: React.FC<DocumentAttachmentsProps> = ({
  entityType,
  entityId,
  siteId,
  title = 'Documents',
  maxFileSize = 50,
  allowedFileTypes,
  maxFiles,
  onUploadComplete,
  onDeleteComplete,
  className = '',
  enableMetadata = false,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [viewingAttachment, setViewingAttachment] = useState<DocumentAttachment | null>(null);
  const [fileMetadata, setFileMetadata] = useState<{ [filename: string]: Record<string, any> }>({});
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState<DocumentAttachment | null>(null);
  const [editMetadata, setEditMetadata] = useState<Record<string, any>>({});

  const queryKey = ['attachments', entityType, entityId];

  const { data: attachments, isLoading } = useQuery({
    queryKey,
    queryFn: () => attachmentService.getEntityAttachments(entityType, entityId),
    enabled: !!entityId,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const metadata = enableMetadata ? fileMetadata[file.name] : undefined;
      const response = await attachmentService.uploadAttachment(
        file,
        entityType,
        entityId,
        siteId,
        metadata
      );
      return response;
    },
    onSuccess: (data, file) => {
      toast({
        title: 'Upload successful',
        description: `${file.name} has been uploaded successfully.`,
      });
      queryClient.invalidateQueries({ queryKey });
      setSelectedFiles([]);
      setUploadProgress({});
      if (onUploadComplete) {
        attachmentService.getAttachment(data.attachment_id).then(onUploadComplete);
      }
    },
    onError: (error: any, file) => {
      toast({
        title: 'Upload failed',
        description: error.response?.data?.detail || `Failed to upload ${file.name}`,
        variant: 'destructive',
      });
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[file.name];
        return newProgress;
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ attachmentId, metadata }: { attachmentId: number; metadata: Record<string, any> }) => {
      return attachmentService.updateAttachment(attachmentId, { file_metadata: metadata });
    },
    onSuccess: () => {
      toast({
        title: 'Metadata updated',
        description: 'Document metadata has been updated successfully.',
      });
      queryClient.invalidateQueries({ queryKey });
      setEditingAttachment(null);
      setEditMetadata({});
    },
    onError: (error: any) => {
      toast({
        title: 'Update failed',
        description: error.response?.data?.detail || 'Failed to update metadata',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (attachmentId: number) => 
      attachmentService.deleteAttachment(attachmentId, true), // Always permanent delete
    onSuccess: (_, attachmentId) => {
      toast({
        title: 'Deleted',
        description: 'Document has been permanently deleted.',
      });
      queryClient.invalidateQueries({ queryKey });
      if (onDeleteComplete) {
        onDeleteComplete(attachmentId);
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.detail || 'Failed to delete document',
        variant: 'destructive',
      });
    },
  });

  const viewUrlMutation = useMutation({
    mutationFn: (attachmentId: number) => attachmentService.getViewUrl(attachmentId),
    onSuccess: (data) => {
      window.open(data.url, '_blank');
    },
    onError: () => {
      toast({
        title: 'View failed',
        description: 'Failed to generate view URL',
        variant: 'destructive',
      });
    },
  });

  const downloadUrlMutation = useMutation({
    mutationFn: (attachmentId: number) => attachmentService.getDownloadUrl(attachmentId),
    onSuccess: (data, attachmentId) => {
      const attachment = attachments?.find((a) => a.id === attachmentId);
      const link = document.createElement('a');
      link.href = data.url;
      link.download = attachment?.file_name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onError: () => {
      toast({
        title: 'Download failed',
        description: 'Failed to generate download URL',
        variant: 'destructive',
      });
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    if (maxFiles && attachments && attachments.length + files.length > maxFiles) {
      toast({
        title: 'Too many files',
        description: `Maximum ${maxFiles} files allowed. You already have ${attachments.length} file(s).`,
        variant: 'destructive',
      });
      return;
    }

    const validFiles: File[] = [];
    
    files.forEach((file) => {
      if (file.size > maxFileSize * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: `${file.name} exceeds the maximum size of ${maxFileSize}MB`,
          variant: 'destructive',
        });
        return;
      }

      if (allowedFileTypes && allowedFileTypes.length > 0) {
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!allowedFileTypes.includes(fileExtension)) {
          toast({
            title: 'Invalid file type',
            description: `${file.name} is not an allowed file type. Allowed types: ${allowedFileTypes.join(', ')}`,
            variant: 'destructive',
          });
          return;
        }
      }

      validFiles.push(file);
    });

    setSelectedFiles(validFiles);
    
    // Initialize metadata for each file if metadata is enabled
    if (enableMetadata && validFiles.length > 0) {
      const newMetadata: { [filename: string]: Record<string, any> } = {};
      validFiles.forEach(file => {
        newMetadata[file.name] = fileMetadata[file.name] || {};
      });
      setFileMetadata(newMetadata);
    }
  };

  const handleUpload = async () => {
    if (enableMetadata && selectedFiles.length > 0) {
      // Show metadata dialog before upload
      setMetadataDialogOpen(true);
    } else {
      // Upload without metadata
      for (const file of selectedFiles) {
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
        await uploadMutation.mutateAsync(file);
      }
    }
  };
  
  const handleUploadWithMetadata = async () => {
    setMetadataDialogOpen(false);
    for (const file of selectedFiles) {
      setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
      await uploadMutation.mutateAsync(file);
    }
  };
  
  const updateFileMetadata = (filename: string, key: string, value: string) => {
    setFileMetadata(prev => ({
      ...prev,
      [filename]: {
        ...prev[filename],
        [key]: value
      }
    }));
  };
  
  const addMetadataField = (filename: string) => {
    const newKey = `key_${Object.keys(fileMetadata[filename] || {}).length + 1}`;
    updateFileMetadata(filename, newKey, '');
  };
  
  const removeMetadataField = (filename: string, key: string) => {
    setFileMetadata(prev => {
      const newMetadata = { ...prev };
      if (newMetadata[filename]) {
        delete newMetadata[filename][key];
      }
      return newMetadata;
    });
  };
  
  const handleEditMetadata = (attachment: DocumentAttachment) => {
    setEditingAttachment(attachment);
    setEditMetadata(attachment.file_metadata || {});
  };
  
  const handleUpdateMetadata = () => {
    if (editingAttachment) {
      updateMutation.mutate({
        attachmentId: editingAttachment.id,
        metadata: editMetadata
      });
    }
  };
  
  const updateEditMetadata = (key: string, value: string) => {
    setEditMetadata(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  const addEditMetadataField = () => {
    const newKey = `key_${Object.keys(editMetadata).length + 1}`;
    updateEditMetadata(newKey, '');
  };
  
  const removeEditMetadataField = (key: string) => {
    setEditMetadata(prev => {
      const newMetadata = { ...prev };
      delete newMetadata[key];
      return newMetadata;
    });
  };

  const handleDelete = (attachmentId: number) => {
    setDeleteConfirmId(attachmentId);
  };

  const confirmDelete = () => {
    if (deleteConfirmId) {
      console.log('🗑️ Confirming delete:', { deleteConfirmId });
      deleteMutation.mutate(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  };

  const isImage = (mimeType: string) => mimeType.startsWith('image/');

  if (!entityId) {
    return null;
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{title}</span>
          <Badge variant="secondary">{attachments?.length || 0} files</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Upload Section */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept={allowedFileTypes?.join(',')}
            />
            
            {selectedFiles.length === 0 ? (
              <div>
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  Drop files here or{' '}
                  <Button
                    variant="link"
                    className="p-0 h-auto"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    browse
                  </Button>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Max file size: {maxFileSize}MB
                  {allowedFileTypes && allowedFileTypes.length > 0 && (
                    <span> • Allowed types: {allowedFileTypes.join(', ')}</span>
                  )}
                </p>
              </div>
            ) : (
              <div>
                <div className="space-y-2 mb-4">
                  {selectedFiles.map((file) => (
                    <div key={file.name} className="flex items-center justify-between text-sm">
                      <span className="truncate">{file.name}</span>
                      <span className="text-gray-500">
                        {attachmentService.formatFileSize(file.size)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 justify-center">
                  <Button
                    size="sm"
                    onClick={handleUpload}
                    disabled={uploadMutation.isPending}
                  >
                    {enableMetadata ? (
                      <>
                        <Settings className="mr-2 h-4 w-4" />
                        Configure & Upload {selectedFiles.length} file(s)
                      </>
                    ) : (
                      `Upload ${selectedFiles.length} file(s)`
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedFiles([])}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {Object.entries(uploadProgress).map(([fileName, progress]) => (
            <div key={fileName} className="space-y-1">
              <div className="text-sm font-medium">{fileName}</div>
              <Progress value={progress} className="h-2" />
            </div>
          ))}

          {/* Attachments List */}
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : attachments && attachments.length > 0 ? (
            <div className="space-y-2">
              {attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="text-2xl">
                      {attachmentService.getFileIcon(attachment.mime_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{attachment.file_name}</p>
                      <p className="text-xs text-gray-500">
                        {attachmentService.formatFileSize(attachment.file_size)} •{' '}
                        {new Date(attachment.created_at).toLocaleDateString()}
                      </p>
                      {attachment.file_metadata && Object.keys(attachment.file_metadata).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {Object.entries(attachment.file_metadata).map(([key, value]) => (
                            <Badge key={key} variant="secondary" className="text-xs">
                              {key}: {String(value)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {enableMetadata && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEditMetadata(attachment)}
                        disabled={updateMutation.isPending}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => viewUrlMutation.mutate(attachment.id)}
                      disabled={viewUrlMutation.isPending}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => downloadUrlMutation.mutate(attachment.id)}
                      disabled={downloadUrlMutation.isPending}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(attachment.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <File className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-2 text-sm">No documents uploaded yet</p>
            </div>
          )}
        </div>

        {/* Metadata Dialog */}
        {enableMetadata && (
          <Dialog open={metadataDialogOpen} onOpenChange={setMetadataDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Metadata to Files</DialogTitle>
                <DialogDescription>
                  Add custom metadata key-value pairs to your files. This information will be stored with the documents.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {selectedFiles.map((file) => (
                  <div key={file.name} className="border rounded-lg p-4 space-y-3">
                    <div className="font-medium text-sm">{file.name}</div>
                    
                    <div className="space-y-2">
                      {Object.entries(fileMetadata[file.name] || {}).map(([key, value], index) => (
                        <div key={`metadata-${file.name}-${index}`} className="flex gap-2 items-center">
                          <Input
                            placeholder="Key"
                            defaultValue={key}
                            onBlur={(e) => {
                              const newKey = e.target.value;
                              if (newKey && newKey !== key) {
                                setFileMetadata(prev => {
                                  const newMetadata = { ...prev };
                                  const fileData = { ...newMetadata[file.name] };
                                  delete fileData[key];
                                  fileData[newKey] = value;
                                  newMetadata[file.name] = fileData;
                                  return newMetadata;
                                });
                              }
                            }}
                            className="flex-1"
                          />
                          <Input
                            placeholder="Value"
                            value={value as string}
                            onChange={(e) => updateFileMetadata(file.name, key, e.target.value)}
                            className="flex-1"
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => removeMetadataField(file.name, key)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => addMetadataField(file.name)}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Metadata Field
                    </Button>
                  </div>
                ))}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setMetadataDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUploadWithMetadata} disabled={uploadMutation.isPending}>
                  Upload with Metadata
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Edit Metadata Dialog */}
        {enableMetadata && editingAttachment && (
          <Dialog open={!!editingAttachment} onOpenChange={(open) => !open && setEditingAttachment(null)}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Document Metadata</DialogTitle>
                <DialogDescription>
                  Update metadata for: {editingAttachment.file_name}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  {Object.entries(editMetadata).map(([key, value], index) => (
                    <div key={`edit-metadata-${index}`} className="flex gap-2 items-center">
                      <Input
                        placeholder="Key"
                        defaultValue={key}
                        onBlur={(e) => {
                          const newKey = e.target.value;
                          if (newKey && newKey !== key) {
                            setEditMetadata(prev => {
                              const newMetadata = { ...prev };
                              delete newMetadata[key];
                              newMetadata[newKey] = value;
                              return newMetadata;
                            });
                          }
                        }}
                        className="flex-1"
                      />
                      <Input
                        placeholder="Value"
                        value={value as string}
                        onChange={(e) => updateEditMetadata(key, e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeEditMetadataField(key)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addEditMetadataField}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Metadata Field
                </Button>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingAttachment(null)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateMetadata} disabled={updateMutation.isPending}>
                  Update Metadata
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Document</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this document? This will permanently remove the file and cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmDelete}
                className="bg-destructive hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};